/**
 * Base Tool Class
 *
 * Abstract base class that all tools extend.
 * Provides a standard interface for tool metadata, summary data, and backup configuration.
 */

export class BaseTool {
  constructor() {
    // Metadata - must be overridden by subclasses
    this.id = "";
    this.name = "";
    this.description = "";
    this.icon = null;
    this.color = "";
    this.path = "";

    // Collections for backup
    this.collections = [];
    // Tools that still exist for data purposes (backup/export, detail routes)
    // but should not appear in the sidebar or dashboard — e.g. the promoter
    // tools, which are now reached through the combined Promoters tool's tabs.
    this.navHidden = false;
    // Optional. When set, tints this tool's CONTROLS (buttons, filters,
    // charts) separately from its identity colour. Use the keyword "neutral"
    // for a tool whose content is colour-coded — see utils/toolTheme.js.
    this.controlColor = null;

    // Access control
    this.requiresAdmin = false;
    // Whether the tool's switch (Settings → tools for standard users) starts ON. Every tool's does,
    // except one that says false here: standard users see it only once an administrator switches
    // it on, and it stays closed to them if the switch cannot be read (utils/toolAccess.js).
    this.switchedOnByDefault = true;
    // Hidden from the sidebar and dashboard for EVERYONE until a person switches it on
    // in their profile (users/{uid}.shownTools). For tools in beta.
    this.hiddenByDefault = false;
    // Placement, not permission. An admin-only tool sits under System by default;
    // set mainNav to promote it into the working Tools nav without widening access.
    this.mainNav = false;

    // Summary widget configuration
    this.summaryConfig = {
      enabled: true,
      mainStat: null, // Key for the main stat (e.g., 'todayScans')
      expectedStatCount: 4, // Total number of stats for loading skeleton
    };

    // Filter configuration - tools can define their own filters
    this.filterConfig = {
      search: false,
      buttonGroups: [], // Array of button group configs
      sortOptions: null, // Array of sort options
    };
  }

  /**
   * Get summary data for dashboard widget
   * Must be implemented by subclasses
   * @returns {Promise<{success: boolean, data?: object, error?: string, lastUpdated?: Date}>}
   */
  async getSummaryData() {
    throw new Error(
      `getSummaryData() must be implemented by ${this.constructor.name}`,
    );
  }

  /**
   * Get collections for backup
   * Default implementation returns this.collections
   * Can be overridden for dynamic collection lists
   */
  getCollections() {
    return this.collections;
  }

  /**
   * Prepare collections for backup
   * Hook for custom backup logic (e.g., filtering test data)
   * @param {string[]} collections - Collection names
   * @param {string} format - Backup format ('bucket', 'firestore', 'json', 'csv')
   * @returns {Promise<string[]>} - Processed collection names
   */
  async prepareBackup(collections, format) {
    // Default: return collections as-is
    return collections;
  }

  /**
   * Get filter configuration for FilterBar component
   * Can be overridden to provide custom filters
   */
  getFilterConfig() {
    return this.filterConfig;
  }

  /**
   * Check if user can access this tool
   * @param {object} user - User object with isAdmin property
   * @returns {boolean}
   */
  canAccess(user) {
    if (this.requiresAdmin) {
      return user?.isAdmin === true;
    }
    return true;
  }

  /**
   * Validate that required properties are set
   * Called by ToolRegistry during registration
   */
  validate() {
    const required = ["id", "name", "description", "icon", "color", "path"];
    const missing = required.filter((prop) => !this[prop]);

    if (missing.length > 0) {
      throw new Error(
        `Tool ${
          this.constructor.name
        } missing required properties: ${missing.join(", ")}`,
      );
    }

    if (this.collections.length === 0) {
      console.warn(
        `Tool ${this.constructor.name} has no Firestore collections defined`,
      );
    }
  }

  /**
   * Get tool metadata as plain object
   * Useful for serialization and debugging
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      color: this.color,
      path: this.path,
      collections: this.collections,
      requiresAdmin: this.requiresAdmin,
      hiddenByDefault: this.hiddenByDefault,
      mainNav: this.mainNav,
      summaryConfig: this.summaryConfig,
      filterConfig: this.filterConfig,
    };
  }
}

export default BaseTool;
